import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { RangePreset, RangeSelection } from "@/lib/api";

const PRESETS = new Set<RangePreset>([
  "current_period",
  "previous_period",
  "last_7d",
  "last_14d",
  "last_30d",
  "custom",
]);

/**
 * Read the selected range from the URL, so a link to "last 30 days" or a
 * particular pay period can be shared and survives a refresh.
 */
export function useRangeParams(searchParams: URLSearchParams): RangeSelection {
  const periodIndex = searchParams.get("periodIndex");
  const preset = searchParams.get("preset");
  const from = searchParams.get("from");
  const to = searchParams.get("to");

  return useMemo(() => rangeOf(periodIndex, preset, from, to), [periodIndex, preset, from, to]);
}

/** The same outside React: what a link's query string selects (the prefetcher, `lib/route-prefetch.ts`). */
export function parseRange(searchParams: URLSearchParams): RangeSelection {
  return rangeOf(searchParams.get("periodIndex"), searchParams.get("preset"), searchParams.get("from"), searchParams.get("to"));
}

function rangeOf(periodIndex: string | null, preset: string | null, from: string | null, to: string | null): RangeSelection {
  if (periodIndex !== null && Number.isInteger(Number(periodIndex))) {
    return { preset: "custom", periodIndex: Number(periodIndex) };
  }
  if (preset === "custom" && from && to) {
    return { preset: "custom", from, to };
  }
  if (preset && PRESETS.has(preset as RangePreset) && preset !== "custom") {
    return { preset: preset as RangePreset };
  }
  return { preset: "current_period" };
}

/**
 * The selected range plus a setter, both backed by the query string.
 *
 * Keeping the range in the URL is what makes it survive navigation: the
 * leaderboard links to an editor with the same query string, and the editor page
 * links back with whatever it was changed to, so the window never silently
 * resets under you.
 */
export function useRange(): [RangeSelection, (next: RangeSelection) => void, URLSearchParams] {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useRangeParams(searchParams);

  const setRange = useCallback(
    (next: RangeSelection) => {
      // Rebuilt from the current query rather than from empty: other state also
      // lives here now, and changing the dates should not reset the chart.
      const params = new URLSearchParams(searchParams);
      for (const key of ["periodIndex", "preset", "from", "to"]) params.delete(key);

      if (next.periodIndex !== undefined) params.set("periodIndex", String(next.periodIndex));
      else {
        params.set("preset", next.preset);
        if (next.from) params.set("from", next.from);
        if (next.to) params.set("to", next.to);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return [range, setRange, searchParams];
}
